/**
 * Types for the backend `notification-log` API (push/email notification history).
 *
 * Backed by SQLite, FIFO-capped at the last 100 notifications per user.
 * Docs: http://repo.shelly.cloud:6040/docs/#/statistics?id=notification-log
 * Backend: https://gitlab.allterco.net/Shelly/be/iot-backend/-/work_items/2022
 *
 * The optional `meta` JSON string correlates notifications to devices or scenes.
 */

/** Notification severity, derived server-side from the notification `type`. */
export enum NotificationLogLevel {
	Normal = 1,
	Critical = 2,
	Fatal = 3,
}

/** Per-notification state. Mutable by the app via the update-state endpoint. */
export enum NotificationLogState {
	Unseen = 1,
	Seen = 2,
	Dismissed = 3,
}

export interface NotificationLogDeviceMeta {
	/** Decimal device ID. Convert to hex before matching the app device store. */
	deviceId: string;
	channel?: number;
}

export interface NotificationLogSceneMeta {
	scnid: string;
	scnname: string;
}

export type NotificationLogMeta = NotificationLogDeviceMeta | NotificationLogSceneMeta;

export interface NotificationLogEntry {
	/** Auto-increment row id, also used in `rowIds` for update-state. */
	id: number;
	/**
	 * Message timestamp, format `"YYYY-MM-DD HH:mm:ss"`.
	 * TODO(FE-1715): timezone is undocumented — confirm UTC vs server-local with backend before formatting.
	 */
	ts: string;
	/** Translation identifier used to construct the localized title and message. */
	type: string;
	/** Legacy persisted title, retained as a fallback for older backend rows. */
	title?: string;
	/** Legacy persisted body, retained as a fallback for older backend rows. */
	text?: string;
	level: NotificationLogLevel;
	state: NotificationLogState;
	/** JSON-encoded `NotificationLogDeviceMeta` or `NotificationLogSceneMeta`. */
	meta?: string;
}

export interface UpdateNotificationStateRequest {
	rowIds: number[];
	state: NotificationLogState;
}
